import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH12_QUESTIONS } from "../data/neetPhysicsCh12Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 12: Thermodynamics (NCERT Class 11).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Thermodynamic Systems & First Law", "Thermodynamic Processes", "Specific Heats & Second Law", "Heat Engines & Refrigerators"];

const SECTION_COLORS = {
  "Thermodynamic Systems & First Law": "#2563eb",
  "Thermodynamic Processes":           "#ea580c",
  "Specific Heats & Second Law":       "#7c3aed",
  "Heat Engines & Refrigerators":      "#0d9488",
};

const CONCEPTS = [
  // ── Thermodynamic Systems & First Law ──────────────────────────────────────
  { section: "Thermodynamic Systems & First Law", title: "Thermodynamic systems, surroundings and boundary",
    explanation: "A thermodynamic system is the specific part of the universe under study; everything else is the surroundings, and the real or imaginary surface separating them is the boundary. Systems are classified by what can cross this boundary: an OPEN system exchanges both matter and energy (e.g. an open cup of hot tea); a CLOSED system exchanges energy but not matter (e.g. a sealed gas cylinder being heated); an ISOLATED system exchanges neither matter nor energy with its surroundings (e.g. an ideal thermos flask).",
    example: `System type   | Matter exchange | Energy exchange
Open          | Yes             | Yes
Closed        | No              | Yes
Isolated      | No              | No

e.g. gas in a cylinder with a movable, insulated piston that
lets heat in/out but not gas -> CLOSED system` },
  { section: "Thermodynamic Systems & First Law", title: "Thermal equilibrium and the zeroth law of thermodynamics",
    explanation: "Two systems are in thermal equilibrium when no net heat flows between them upon contact -- they are then said to be at the same temperature. The zeroth law states: if system A is separately in thermal equilibrium with system B, and system B is in thermal equilibrium with system C, then A and C are also in thermal equilibrium with each other. This law justifies the existence of temperature as a well-defined state variable, and is the working principle behind thermometers.",
    example: `A <-> B (thermal equilibrium, same temperature)
B <-> C (thermal equilibrium, same temperature)
=> A <-> C (must also be in equilibrium)

Thermometer = a small "C" placed in equilibrium with A, then
B -- if it reads the same temperature both times, A and B
must be at the same temperature too.` },
  { section: "Thermodynamic Systems & First Law", title: "Heat, work and internal energy — state functions vs path functions",
    explanation: "Internal energy (U) is the sum of the kinetic and potential energies of all the molecules of a system; it is a STATE FUNCTION, depending only on the current state (for an ideal gas, only on temperature), not on how that state was reached. Heat (Q) and work (W), by contrast, are PATH FUNCTIONS -- the amount of heat or work exchanged depends on the specific process taken between two states, even if the initial and final states are identical.",
    example: `Two different paths from state 1 to state 2 (same start
and end states):
Path A: Q_A = 600 J, W_A = 250 J  -> ΔU = 600-250 = 350 J
Path B: Q_B = 900 J, W_B = 550 J  -> ΔU = 900-550 = 350 J

Q and W differ between the two paths, but ΔU is the SAME
both times -- confirming U is a state function.` },
  { section: "Thermodynamic Systems & First Law", title: "Sign convention in the first law of thermodynamics",
    explanation: "By the standard NCERT convention: ΔQ is positive when heat is ABSORBED by the system (negative when released); ΔW is positive when work is done BY the system on the surroundings (negative when work is done ON the system by an external agent); ΔU is positive when the internal energy of the system INCREASES. All three quantities in ΔQ = ΔU + ΔW must use this same consistent convention.",
    example: `Gas expands and pushes a piston out -> ΔW > 0 (by the gas)
Gas is compressed by an external force -> ΔW < 0
Gas absorbs heat from a burner -> ΔQ > 0
Gas releases heat to a cold sink -> ΔQ < 0` },
  { section: "Thermodynamic Systems & First Law", title: "First law of thermodynamics — ΔQ = ΔU + ΔW",
    explanation: "The first law of thermodynamics is a statement of energy conservation applied to heat: the heat supplied to a system either increases its internal energy or is used by the system to do work (or both), ΔQ = ΔU + ΔW. It applies to ANY process, reversible or irreversible, and is the thermodynamic generalization of the work-energy theorem.",
    example: `A gas is compressed, with 300 J of work done ON it by an
external agent, while it releases 100 J of heat to the
surroundings. Find ΔU.

ΔQ = -100 J   (heat released, so negative)
ΔW = -300 J   (work done ON the gas, so negative)

ΔU = ΔQ - ΔW = -100 - (-300) = +200 J
-> the gas's internal energy INCREASES by 200 J, even
   though it lost heat, because compression pumped in
   more energy than was lost as heat.` },

  // ── Thermodynamic Processes ─────────────────────────────────────────────────
  { section: "Thermodynamic Processes", title: "Isothermal process",
    explanation: "An isothermal process occurs at CONSTANT TEMPERATURE -- the system stays in contact with a large heat reservoir and the process happens slowly enough (quasi-statically) for temperature to remain uniform throughout. For an ideal gas, U depends only on T, so ΔU = 0 in any isothermal process, and the first law reduces to ΔQ = ΔW -- all the heat absorbed is entirely converted into work done by the gas. The gas obeys PV = constant (Boyle's law) along this process, and the work done in expanding from V1 to V2 is W = nRT ln(V2/V1).",
    example: `1 mol of an ideal gas at T = 300 K expands isothermally
from V1 = 1 L to V2 = 2 L. Find the work done.

W = nRT ln(V2/V1) = (1)(8.314)(300) x ln(2)
  = 2494.2 x 0.693 ≈ 1729 J

Since ΔU = 0 (isothermal), ΔQ = ΔW ≈ 1729 J of heat is
absorbed from the reservoir to do this work.` },
  { section: "Thermodynamic Processes", title: "Adiabatic process",
    explanation: "An adiabatic process occurs with NO heat exchange with the surroundings (ΔQ = 0) -- either the system is thermally insulated, or the process happens too fast for heat to transfer. The first law then gives ΔU = -ΔW: if the gas expands and does positive work, its internal energy (and temperature) must fall, and vice versa for compression. An ideal gas undergoing a reversible adiabatic process obeys PV^γ = constant, where γ = Cp/Cv, and the work done by the gas equals W = nR(T1 - T2)/(γ-1) = (P1V1 - P2V2)/(γ-1).",
    example: `2 mol of a monatomic ideal gas (γ = 5/3) expands
adiabatically; its temperature falls from 400 K to 300 K.

W = nR(T1-T2)/(γ-1) = (2)(8.314)(100) / (5/3 - 1)
  = 1662.8 / (2/3) ≈ 2494 J

The gas does about 2494 J of work at the cost of its own
internal energy, since no heat enters from outside.` },
  { section: "Thermodynamic Processes", title: "Isobaric process",
    explanation: "An isobaric process occurs at CONSTANT PRESSURE, typically because the gas is free to expand against a constant external pressure (e.g. a gas in a cylinder with a freely movable piston). Since P is constant, the work done by the gas is simply W = P.ΔV = P(V2 - V1), which for an ideal gas equals nRΔT (from PV = nRT). The heat supplied is ΔQ = n.Cp.ΔT, where Cp is the molar specific heat at constant pressure.",
    example: `2 mol of an ideal gas is heated at constant pressure,
raising its temperature by 50 K.

W = nRΔT = (2)(8.314)(50) = 831.4 J

This work is done by the gas in pushing back the piston
as it expands at constant pressure.` },
  { section: "Thermodynamic Processes", title: "Isochoric (constant-volume) process",
    explanation: "An isochoric process occurs at CONSTANT VOLUME -- the gas is held in a rigid, fixed container so it cannot expand or contract. Since ΔV = 0, no work is done by or on the gas: ΔW = 0. The first law then simplifies to ΔQ = ΔU -- all the heat supplied goes entirely into increasing the internal energy (and hence temperature) of the gas, with ΔQ = n.Cv.ΔT, where Cv is the molar specific heat at constant volume.",
    example: `1 mol of a monatomic ideal gas (Cv = 3/2 R) is heated at
constant volume, raising its temperature by 100 K.

ΔW = 0
ΔQ = ΔU = n.Cv.ΔT = (1)(1.5 x 8.314)(100) ≈ 1247 J

All 1247 J of heat supplied raises the internal energy --
none of it is used to do external work.` },
  { section: "Thermodynamic Processes", title: "Cyclic processes and work done on a P–V diagram",
    explanation: "In a CYCLIC process, the system returns to its exact initial state after a series of steps, so the net change in internal energy over one complete cycle is zero (ΔU_cycle = 0), since U is a state function. The first law then gives ΔQ_cycle = ΔW_cycle -- the net heat absorbed over one cycle equals the net work done by the system. Graphically, on a P-V diagram, the work done in any process equals the area under that P-V curve, and the NET work done over a full cycle equals the enclosed area of the loop (positive if traced clockwise, i.e. a heat engine).",
    example: `A gas undergoes a cyclic process traced CLOCKWISE on a
P-V diagram, enclosing an area of 500 J.

ΔU (whole cycle) = 0       (state function, returns to start)
Net work done BY the gas = area enclosed = +500 J
=> Net heat absorbed over the cycle: ΔQ = ΔW = +500 J` },

  // ── Specific Heats & Second Law ─────────────────────────────────────────────
  { section: "Specific Heats & Second Law", title: "Molar specific heats Cv and Cp — Mayer's relation",
    explanation: "The molar specific heat at constant volume, Cv, is the heat needed to raise the temperature of 1 mole of gas by 1 K at constant volume (all of it goes into ΔU). The molar specific heat at constant pressure, Cp, is the heat needed to raise the temperature of 1 mole of gas by 1 K at constant pressure (part of it also does expansion work). Since Cp must supply extra energy for this expansion work on top of raising internal energy, Cp is always greater than Cv, and for an ideal gas, Mayer's relation gives Cp - Cv = R, where R is the universal gas constant.",
    example: `Mayer's relation:  Cp - Cv = R   (per mole)

For a diatomic gas: Cv = (5/2)R = 20.79 J/mol.K
                    Cp = (7/2)R = 29.10 J/mol.K
Check: Cp - Cv = 29.10 - 20.79 = 8.31 J/mol.K ≈ R  ✓` },
  { section: "Specific Heats & Second Law", title: "Ratio of specific heats γ = Cp/Cv",
    explanation: "The ratio γ = Cp/Cv depends on a gas's molecular structure (its degrees of freedom, f), since Cv = (f/2)R and Cp = Cv + R = ((f+2)/2)R, giving γ = (f+2)/f. A MONATOMIC gas (f=3, e.g. He, Ar) has γ = 5/3 ≈ 1.67. A DIATOMIC gas (f=5, rigid, e.g. O2, N2 near room temperature) has γ = 7/5 = 1.4. γ always exceeds 1, and decreases as molecular complexity (degrees of freedom) increases.",
    example: `Monatomic (f=3): Cv=(3/2)R, Cp=(5/2)R, γ=Cp/Cv=5/3 ≈ 1.67
Diatomic  (f=5): Cv=(5/2)R, Cp=(7/2)R, γ=Cp/Cv=7/5 = 1.40

More degrees of freedom -> more ways to store energy ->
Cv is larger -> γ = Cp/Cv is smaller (closer to 1)` },
  { section: "Specific Heats & Second Law", title: "Second law of thermodynamics — Kelvin–Planck and Clausius statements",
    explanation: "The first law alone doesn't forbid impossible processes like heat flowing spontaneously from cold to hot, or a heat engine converting heat entirely into work with no other effect -- the second law rules these out. The KELVIN-PLANCK statement: no process is possible whose sole result is the absorption of heat from a single reservoir and its complete conversion into work (so no heat engine can be 100% efficient). The CLAUSIUS statement: no process is possible whose sole result is the transfer of heat from a colder body to a hotter body (this always requires external work, as in a refrigerator). These two statements are logically equivalent.",
    example: `Kelvin-Planck: Q (from hot reservoir) --/--> 100% W
                (impossible with no other effect)

Clausius: Q flows cold -> hot spontaneously
                (impossible without external work input)

A refrigerator/AC does NOT violate Clausius, because it
requires external electrical work W to pump heat uphill.` },
  { section: "Specific Heats & Second Law", title: "Reversible and irreversible processes; entropy",
    explanation: "A REVERSIBLE process is an idealized, infinitely slow (quasi-static) process with no dissipative effects (friction, viscosity, etc.), passing through a continuous succession of equilibrium states, so that reversing it exactly retraces the same path for both the system and surroundings. Virtually all real processes are IRREVERSIBLE, involving finite differences, dissipation, or non-equilibrium intermediate states. Entropy (S) is a state function measuring the disorder/randomness of a system; the second law can be restated as: the total entropy of an isolated system never decreases (ΔS ≥ 0), staying constant only in a reversible process and always increasing in an irreversible one.",
    example: `Hot water (60°C) + cold water (20°C) mixed in an
insulated container -> reach a common ~40°C.

This process is IRREVERSIBLE: the mixture never
spontaneously separates back into hot and cold portions,
even though energy is conserved -- because that reverse
process would DECREASE the total entropy.` },

  // ── Heat Engines & Refrigerators ────────────────────────────────────────────
  { section: "Heat Engines & Refrigerators", title: "Heat engines and efficiency",
    explanation: "A heat engine is a device that converts heat into mechanical work by operating in a repeating cycle between a hot reservoir (source, temperature T1) and a cold reservoir (sink, temperature T2). In each cycle it absorbs heat Q1 from the source, does work W, and rejects the remaining heat Q2 to the sink, so W = Q1 - Q2 (first law, ΔU=0 over a cycle). Its efficiency is defined as the fraction of absorbed heat converted to useful work: η = W/Q1 = (Q1-Q2)/Q1 = 1 - Q2/Q1. No real engine can achieve η = 1 (100%), per the second law.",
    example: `An engine absorbs Q1 = 1000 J from the source and
rejects Q2 = 600 J to the sink per cycle.

W = Q1 - Q2 = 1000 - 600 = 400 J
η = W/Q1 = 400/1000 = 0.4 = 40%` },
  { section: "Heat Engines & Refrigerators", title: "Carnot engine and Carnot's theorem",
    explanation: "A Carnot engine is an idealized, reversible heat engine operating between just two reservoirs, cycling through FOUR reversible steps: (1) isothermal expansion at hot temperature T1, absorbing heat Q1; (2) adiabatic expansion, cooling from T1 to T2; (3) isothermal compression at cold temperature T2, rejecting heat Q2; (4) adiabatic compression, returning to T1. Carnot's theorem states that no engine operating between two given temperatures can be MORE efficient than a reversible (Carnot) engine working between the same two temperatures, and all reversible engines between the same two temperatures have exactly the same efficiency, regardless of the working substance.",
    example: `Carnot cycle (4 reversible steps):
1. Isothermal expansion   at T1  (absorbs Q1)
2. Adiabatic expansion    T1 -> T2
3. Isothermal compression at T2  (rejects Q2)
4. Adiabatic compression  T2 -> T1  (back to start)

Carnot's theorem: η(any engine, T1,T2) ≤ η(Carnot, T1,T2)
-- equality holds only for another reversible engine.` },
  { section: "Heat Engines & Refrigerators", title: "Efficiency of a Carnot engine",
    explanation: "For a Carnot engine, it can be shown that the ratio of heats equals the ratio of the (absolute/Kelvin) temperatures of the reservoirs: Q2/Q1 = T2/T1. Substituting into the general efficiency formula gives the Carnot efficiency η = 1 - T2/T1, using temperatures in KELVIN. This is the maximum possible efficiency for ANY engine operating between those two temperatures, and it shows that η = 1 (100%) is possible only if T2 = 0 K, which is unattainable.",
    example: `A Carnot engine operates between a source at 600 K
and a sink at 300 K.

η = 1 - T2/T1 = 1 - 300/600 = 1 - 0.5 = 0.5 = 50%

Even this IDEAL engine wastes half the absorbed heat --
no real engine between these temperatures can do better.` },
  { section: "Heat Engines & Refrigerators", title: "Refrigerators, heat pumps and coefficient of performance",
    explanation: "A refrigerator (or heat pump) is essentially a heat engine run in reverse: external work W is supplied to extract heat Q2 from a cold reservoir (the inside of the fridge) and reject a larger amount of heat Q1 = Q2 + W to a hot reservoir (the room). Its performance is measured by the coefficient of performance (COP), β = Q2/W = Q2/(Q1-Q2), rather than an 'efficiency' (COP can exceed 1). For an ideal (Carnot) refrigerator, β = T2/(T1-T2), using Kelvin temperatures of the cold and hot reservoirs respectively.",
    example: `A Carnot refrigerator keeps its interior at T2 = 270 K
while the room is at T1 = 300 K.

β = T2/(T1-T2) = 270/(300-270) = 270/30 = 9

This means for every 1 J of electrical work supplied, 9 J
of heat is removed from inside the fridge.` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh12Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh12Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh12Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH12_QUESTIONS : NEET_PHYSICS_CH12_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 12</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Thermodynamics</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH12_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH12_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
